import { useLocationHash, useNavigate } from '@rocketlane/rocket-router';
import { fromPairs, toPairs } from 'lodash';
import { parse, stringify } from 'query-string';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useLatest } from './use-latest';
export const DEFAULT_TRANSFORMER = {
    from: (parsed)=>parsed,
    to: (value)=>value
};
const sanitize = (value)=>fromPairs(toPairs(value).filter(([, x])=>x != null));
/**
 * A re-render trigger for hash changes the router does not deliver.
 *
 * `useLocationHash()` is still the VALUE — this only guarantees a render happens
 * so that value gets re-read. Two things have to line up for a consumer to miss a
 * hash change, and on `/projects/:id/plan` they do:
 *
 * 1. `TaskPaneContextProvider` wraps its children in a `React.memo` keyed on
 *    element identity (`StableChildren`), so the task drawer never re-renders
 *    from its parent. Its own subscription is the only path it has.
 * 2. That subscription does not always fire. Reopening the SAME task after
 *    closing it left the drawer with zero renders while sibling `useHash`
 *    consumers rendered with the new hash — so the hash was current and the
 *    notification simply never arrived. (`RouterProvider` also deliberately keeps
 *    `LocationContext` stale for hash-only changes — see `shouldSkip` in
 *    `@rocketlane/rocket-router` — so a consumer on that branch has nothing else
 *    to fall back on either.)
 *
 * Note the `history` wrapper is not optional: `pushState` fires NEITHER
 * `hashchange` NOR `popstate`, and every in-app navigation goes through it, so
 * plain listeners would only ever catch browser back/forward.
 *
 * The notify is deferred to a microtask on purpose. `router.navigate` calls
 * `pushState` and then emits synchronously; bumping inside the wrapper would
 * render before the router had published the new location, read the old value,
 * and leave us waiting on the same notification that just went missing.
 */ const hashListeners = new Set();
let hashRevision = 0;
let lastSeenHash = typeof window === 'undefined' ? '' : window.location.hash;
let isHistoryPatched = false;
const notifyIfHashChanged = ()=>{
    const next = window.location.hash;
    if (next === lastSeenHash) {
        return;
    }
    lastSeenHash = next;
    hashRevision += 1;
    hashListeners.forEach((listener)=>listener());
};
const scheduleHashCheck = ()=>{
    Promise.resolve().then(notifyIfHashChanged);
};
const patchHistoryOnce = ()=>{
    if (isHistoryPatched || typeof window === 'undefined') {
        return;
    }
    isHistoryPatched = true;
    [
        'pushState',
        'replaceState'
    ].forEach((method)=>{
        const original = window.history[method];
        window.history[method] = function patched(...args) {
            const result = original.apply(this, args);
            scheduleHashCheck();
            return result;
        };
    });
    window.addEventListener('popstate', scheduleHashCheck);
    window.addEventListener('hashchange', scheduleHashCheck);
};
const subscribeToHashRevision = (listener)=>{
    patchHistoryOnce();
    hashListeners.add(listener);
    return ()=>{
        hashListeners.delete(listener);
    };
};
const getHashRevision = ()=>hashRevision;
const getServerHashRevision = ()=>0;
export const useHash = ({ from, to })=>{
    useSyncExternalStore(subscribeToHashRevision, getHashRevision, getServerHashRevision);
    const hash = useLocationHash();
    const navigate = useNavigate();
    const parsed = useMemo(()=>sanitize(parse(hash)), [
        hash
    ]);
    const value = useMemo(()=>from(parsed), [
        parsed,
        from
    ]);
    const getNext = useCallback((next)=>`#${stringify(sanitize({
            ...parsed,
            ...to(next)
        }))}`, [
        parsed,
        to
    ]);
    const setValue = useCallback((next, replace = false)=>{
        var _window_history_state;
        const next_hash = getNext(next);
        if (hash === next_hash) {
            return;
        }
        navigate({
            pathname: window.location.pathname,
            hash: next_hash,
            search: window.location.search
        }, {
            replace,
            state: (_window_history_state = window.history.state) === null || _window_history_state === void 0 ? void 0 : _window_history_state.usr
        });
    }, [
        getNext,
        hash,
        navigate
    ]);
    const setValueLatest = useLatest(setValue);
    const getNextLatest = useLatest(getNext);
    return [
        value,
        setValueLatest,
        getNextLatest
    ];
};
